/**
 * What a sudoku grid means, and nothing else.
 *
 * This file is the single definition of "solved" in Stripey's Sudoku. The
 * generator does not decide whether a puzzle is fair — it digs a hole and asks
 * `solutionCount` how many ways the grid can now be filled, and puts the hole
 * back unless the answer is exactly one.
 *
 * That is the same rule Rocky's Riddles is built on, and it is here for the
 * same reason: a generator that asserts its own puzzles is a generator that
 * agrees with itself while being wrong. A sudoku with two solutions is not a
 * hard sudoku, it is a broken one — and the child it breaks for is the one who
 * filled it in correctly and was told she had not.
 *
 * **A grid is numbers.** Cells hold `1..size`, and `EMPTY` for a cell nobody
 * has filled. Which teddy a number is drawn as is decided in the browser and
 * never reaches this file, so the 9×9 of digits is not a special case: it is
 * the same game with the drawing turned off.
 */

/** An unfilled cell. Zero rather than null, so a grid is a flat array of ints. */
export const EMPTY = 0

/** A grid, in row-major order. `grid[row * size + column]`. */
export type Grid = readonly number[]

/**
 * How big a grid is, and how its boxes are cut.
 *
 * The box is the only part of sudoku that is not obvious from the size: a 6×6
 * has boxes three wide and two tall, because 6 is not a square. Getting this
 * wrong produces a grid that looks right and is not a sudoku, so it is written
 * down once and everything reads it from here — including the CSS that draws
 * the heavy rules, which must agree with the solver about where a box ends.
 */
export interface SudokuShape {
  readonly size: number
  readonly boxWidth: number
  readonly boxHeight: number
}

/** The grids this game deals. Small, middling, and the proper one. */
export const SUDOKU_SIZES = [4, 6, 9] as const

export type SudokuSize = (typeof SUDOKU_SIZES)[number]

export const SUDOKU_SHAPES: Readonly<Record<SudokuSize, SudokuShape>> = {
  4: { size: 4, boxWidth: 2, boxHeight: 2 },
  6: { size: 6, boxWidth: 3, boxHeight: 2 },
  9: { size: 9, boxWidth: 3, boxHeight: 3 },
}

export function isSudokuSize(value: unknown): value is SudokuSize {
  return typeof value === 'number' && (SUDOKU_SIZES as readonly number[]).includes(value)
}

export function shapeOf(size: SudokuSize): SudokuShape {
  return SUDOKU_SHAPES[size]
}

/** Every value a cell in this grid may hold, in order. */
export function valuesOf(shape: SudokuShape): number[] {
  return Array.from({ length: shape.size }, (_, index) => index + 1)
}

/**
 * The rows, columns and boxes, as lists of cell indices.
 *
 * Worked out once per size and kept, because the brute-force count below walks
 * them tens of thousands of times for a single 9×9 and rebuilding twenty-seven
 * arrays inside that loop is the difference between a round that deals and a
 * tablet that goes quiet.
 */
const UNITS = new Map<number, readonly (readonly number[])[]>()

export function unitsOf(shape: SudokuShape): readonly (readonly number[])[] {
  const cached = UNITS.get(shape.size)
  if (cached) return cached

  const { size, boxWidth, boxHeight } = shape
  const units: number[][] = []

  for (let row = 0; row < size; row += 1) {
    units.push(Array.from({ length: size }, (_, column) => row * size + column))
  }

  for (let column = 0; column < size; column += 1) {
    units.push(Array.from({ length: size }, (_, row) => row * size + column))
  }

  for (let top = 0; top < size; top += boxHeight) {
    for (let left = 0; left < size; left += boxWidth) {
      const box: number[] = []
      for (let row = top; row < top + boxHeight; row += 1) {
        for (let column = left; column < left + boxWidth; column += 1) {
          box.push(row * size + column)
        }
      }
      units.push(box)
    }
  }

  UNITS.set(size, units)

  return units
}

/**
 * The cells that share a row, a column or a box with this one.
 *
 * Never includes the cell itself, which is what lets `candidatesFor` be asked
 * about a cell that is already filled without it ruling out its own value.
 */
const PEERS = new Map<number, readonly (readonly number[])[]>()

export function peersOf(shape: SudokuShape): readonly (readonly number[])[] {
  const cached = PEERS.get(shape.size)
  if (cached) return cached

  const cells = shape.size * shape.size
  const sets = Array.from({ length: cells }, () => new Set<number>())

  for (const unit of unitsOf(shape)) {
    for (const cell of unit) {
      const set = sets[cell] as Set<number>
      for (const other of unit) if (other !== cell) set.add(other)
    }
  }

  const peers = sets.map((set) => [...set])
  PEERS.set(shape.size, peers)

  return peers
}

/** Which box a cell is in, counting from zero, left to right then down. */
export function boxOf(shape: SudokuShape, cell: number): number {
  const row = Math.floor(cell / shape.size)
  const column = cell % shape.size
  const across = shape.size / shape.boxWidth

  return Math.floor(row / shape.boxHeight) * across + Math.floor(column / shape.boxWidth)
}

/**
 * Candidates as a bitmask — bit `n - 1` set means the value `n` still fits.
 *
 * The whole of this game's inner loop is "what could go here", asked millions
 * of times to dig one 9×9. Asked with sets and arrays it cost 120ms a puzzle,
 * which is 120ms of a tablet doing nothing visible at the start of a round,
 * twice — the browser deals the puzzle and the server deals it again to score
 * it. As bits it is a handful of integer operations and allocates nothing.
 *
 * Nine values, so a 32-bit int is roomy. `candidatesFor` below is the same
 * question asked in a form a person can read, and is what the tests use.
 */
export function maskOf(values: readonly number[]): number {
  let mask = 0
  for (const value of values) mask |= 1 << (value - 1)

  return mask
}

/** Every value in a mask, smallest first. */
export function valuesIn(mask: number, size: number): number[] {
  const values: number[] = []
  for (let value = 1; value <= size; value += 1) {
    if (mask & (1 << (value - 1))) values.push(value)
  }

  return values
}

/** How many values are in a mask. */
export function countIn(mask: number): number {
  let bits = mask
  let count = 0
  while (bits) {
    bits &= bits - 1
    count += 1
  }

  return count
}

/** The single value in a mask that holds exactly one. Undefined behaviour otherwise. */
export function onlyValueIn(mask: number): number {
  return 31 - Math.clz32(mask) + 1
}

/** Every cell's candidates at once, which is far cheaper than asking one at a time. */
export function candidateMasks(grid: Grid, shape: SudokuShape): Int32Array {
  const { size } = shape
  const all = (1 << size) - 1
  const rows = new Int32Array(size)
  const columns = new Int32Array(size)
  const boxes = new Int32Array(size)

  for (let cell = 0; cell < grid.length; cell += 1) {
    const value = grid[cell]
    if (value === undefined || value === EMPTY) continue

    const bit = 1 << (value - 1)
    const row = Math.floor(cell / size)
    const column = cell % size
    const box = boxOf(shape, cell)

    rows[row] = (rows[row] as number) | bit
    columns[column] = (columns[column] as number) | bit
    boxes[box] = (boxes[box] as number) | bit
  }

  const masks = new Int32Array(grid.length)
  for (let cell = 0; cell < grid.length; cell += 1) {
    // Filled cells get nothing. Anything reading this array must skip them by
    // looking at the grid, not by looking for a zero mask — a cell with no
    // candidates left is also zero, and those two mean opposite things.
    if (grid[cell] !== EMPTY) continue

    const taken =
      (rows[Math.floor(cell / size)] as number) |
      (columns[cell % size] as number) |
      (boxes[boxOf(shape, cell)] as number)

    masks[cell] = all & ~taken
  }

  return masks
}

/** Every value this cell could still take, given what its peers hold. */
export function candidatesFor(grid: Grid, shape: SudokuShape, cell: number): number[] {
  const taken = new Set<number>()
  for (const peer of (peersOf(shape)[cell] as readonly number[]) ?? []) {
    const value = grid[peer]
    if (value !== undefined && value !== EMPTY) taken.add(value)
  }

  return valuesOf(shape).filter((value) => !taken.has(value))
}

/**
 * The cells that duplicate another cell in a row, a column or a box.
 *
 * This — and **only** this — is what the game shows her as a conflict. A cell
 * that differs from the solution but breaks no rule is not marked, because
 * marking it would be doing the puzzle for her: she would fill a cell, be told
 * it was wrong, and have learned the answer rather than found it. It is also
 * the reason the view never carries the solution. It has nothing to do with it.
 *
 * Both cells of a clash are returned, not just the later one. Neither is at
 * fault — a duplicate is a fact about a pair.
 */
export function conflictsIn(grid: Grid, shape: SudokuShape): Set<number> {
  const clashing = new Set<number>()

  for (const unit of unitsOf(shape)) {
    const seen = new Map<number, number[]>()

    for (const cell of unit) {
      const value = grid[cell]
      if (value === undefined || value === EMPTY) continue

      const already = seen.get(value)
      if (already) already.push(cell)
      else seen.set(value, [cell])
    }

    for (const cells of seen.values()) {
      if (cells.length > 1) for (const cell of cells) clashing.add(cell)
    }
  }

  return clashing
}

/**
 * Whether every cell is filled and no cell clashes with another.
 *
 * Named for the grid rather than called `isSolved`, because Hoppity's Hangman
 * already exports one of those and the shared package is re-exported flat. Two
 * functions called `isSolved` from one import is the sort of ambiguity that
 * compiles wrongly somewhere else entirely.
 */
export function isGridSolved(grid: Grid, shape: SudokuShape): boolean {
  if (grid.some((value) => value === EMPTY)) return false

  return conflictsIn(grid, shape).size === 0
}

/**
 * How many ways this grid can be filled in, up to `cap`.
 *
 * Stops as soon as it reaches the cap, which is the whole point: the generator
 * only ever needs to know "one, or more than one", and counting all 6.6×10^21
 * solutions of an empty 9×9 to find that out would not finish this century.
 *
 * The cell it tries next is always the one with the fewest candidates left.
 * That is not a nicety — on a dug 9×9 it is the difference between a search
 * that ends in a millisecond and one that visits millions of dead branches,
 * and this runs sixty-odd times per puzzle, on her tablet, before she sees
 * anything.
 */
export function solutionCount(grid: Grid, shape: SudokuShape, cap = 2): number {
  return search(grid, shape, cap).found
}

/**
 * The one way this grid can be filled in, or null if there is not exactly one.
 *
 * Asks for two and refuses to answer if it gets them, so a caller cannot end
 * up holding "a" solution to a puzzle that has several — which is how a
 * generator comes to mark a correct answer wrong.
 */
export function soleSolution(grid: Grid, shape: SudokuShape): number[] | null {
  const outcome = search(grid, shape, 2)

  return outcome.found === 1 ? outcome.first : null
}

/**
 * The search both of the above are made of.
 *
 * Two things make it quick enough to run sixty times per puzzle on a tablet.
 *
 * The **used values are carried in three little arrays** — one per row, column
 * and box — and updated as cells are filled and emptied again, rather than
 * being worked out from the whole grid at every node. That turns "what fits
 * here" from a walk over twenty peers into three lookups and an `and`.
 *
 * And the cell it tries next is always the one with the **fewest candidates
 * left**. That is not a nicety: on a dug 9×9 it is the difference between a
 * search that ends in a millisecond and one that visits millions of dead
 * branches.
 */
function search(
  grid: Grid,
  shape: SudokuShape,
  cap: number,
): { found: number; first: number[] | null } {
  const { size } = shape
  const all = (1 << size) - 1
  const working = [...grid]
  const rows = new Int32Array(size)
  const columns = new Int32Array(size)
  const boxes = new Int32Array(size)
  const boxAt = new Int32Array(working.length)

  for (let cell = 0; cell < working.length; cell += 1) {
    boxAt[cell] = boxOf(shape, cell)

    const value = working[cell]
    if (value === undefined || value === EMPTY) continue

    const bit = 1 << (value - 1)
    // A grid that already breaks a rule has no solutions, and saying so here
    // keeps the masks honest: without this, two 5s in a row would set the same
    // bit once and be undone twice.
    const row = Math.floor(cell / size)
    if (
      (rows[row] as number) & bit ||
      (columns[cell % size] as number) & bit ||
      (boxes[boxAt[cell] as number] as number) & bit
    ) {
      return { found: 0, first: null }
    }

    const column = cell % size
    const box = boxAt[cell] as number

    rows[row] = (rows[row] as number) | bit
    columns[column] = (columns[column] as number) | bit
    boxes[box] = (boxes[box] as number) | bit
  }

  let found = 0
  let first: number[] | null = null

  const step = (): void => {
    if (found >= cap) return

    let best = -1
    let bestMask = 0
    let bestCount = Number.POSITIVE_INFINITY

    for (let cell = 0; cell < working.length; cell += 1) {
      if (working[cell] !== EMPTY) continue

      const row = Math.floor(cell / size)
      const mask =
        all &
        ~(
          (rows[row] as number) |
          (columns[cell % size] as number) |
          (boxes[boxAt[cell] as number] as number)
        )

      // Nothing fits here, so nothing below this point can be a solution.
      if (mask === 0) return

      const count = countIn(mask)
      if (count < bestCount) {
        best = cell
        bestMask = mask
        bestCount = count
        // One candidate is forced; no other cell can be cheaper to try.
        if (count === 1) break
      }
    }

    // Every cell is filled, and each was filled with something its peers
    // allowed, so this is a solution.
    if (best === -1) {
      found += 1
      if (first === null) first = [...working]
      return
    }

    const row = Math.floor(best / size)
    const column = best % size
    const box = boxAt[best] as number

    for (const value of valuesIn(bestMask, size)) {
      const bit = 1 << (value - 1)
      working[best] = value
      rows[row] = (rows[row] as number) | bit
      columns[column] = (columns[column] as number) | bit
      boxes[box] = (boxes[box] as number) | bit

      step()

      working[best] = EMPTY
      rows[row] = rows[row] & ~bit
      columns[column] = columns[column] & ~bit
      boxes[box] = boxes[box] & ~bit

      if (found >= cap) return
    }
  }

  step()

  return { found, first }
}
